import type { CoreError } from '@ingenico/api-sdk-core/common';
import { Level, showNotification } from '@ingenico/react-notifications';
import { sectionContainerAtom, useBusinessService, useBusinessServiceRequest } from '@ingenico/react-section-utils';
import { requestAndUpdateState } from '@ingenico/react-utils/functions';
import { useAsyncDataState } from '@ingenico/react-utils/hooks';
import type { LogicalDeviceSyncConfigurationModalProps } from '@ingenico/type-mfe-contracts/domains';
import { ALL_LNGS_NAMESPACES } from '@ingenico/util-i18n-config';
import deepEqual from 'fast-deep-equal';
import { createStore, Provider } from 'jotai';
import { toString } from 'lodash-es';
import { map } from 'rxjs';
import type { ReactElement } from 'react';
import { memo, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { deviceManagementContainer } from '../../container.const';
import { DeviceManagementService } from '../../domain/business/services/deviceManagement.service';
import { useDeviceManagementCapability } from '../../hooks/useDeviceManagementCapability.hook';
import { LogicalDeviceSyncConfigurationModal } from '../logical-device-sync-configuration-modal/LogicalDeviceSyncConfigurationModal.ctrl';
import { isGroupedMultiMerchantSyncTarget } from '../logical-device-sync-configuration-modal/LogicalDeviceSyncConfigurationModal.util';

function ExposedLogicalDeviceSyncConfigurationModalContent({
  isOpen,
  logicalDeviceIds,
  variant,
  onClose,
  onSuccess,
}: LogicalDeviceSyncConfigurationModalProps): ReactElement {
  const { t } = useTranslation([ALL_LNGS_NAMESPACES.LOGICAL_DEVICE]);
  const deviceManagementBusinessService = useBusinessService(DeviceManagementService);
  const [syncConfigurationState, setSyncConfigurationState] = useAsyncDataState<unknown, CoreError>();
  const isDmMultiMerchantEnabled = useDeviceManagementCapability('deployments.isDmMultiMerchantEnabled');

  const multiMerchantLogicalDeviceId = logicalDeviceIds.length === 1 ? logicalDeviceIds[0] : undefined;

  const { data: syncTargetDetails } = useBusinessServiceRequest(
    DeviceManagementService,
    useCallback(
      (deviceManagementService) =>
        isOpen && isDmMultiMerchantEnabled && multiMerchantLogicalDeviceId
          ? deviceManagementService
              .getLogicalDeviceDetailsBff(multiMerchantLogicalDeviceId)
              .pipe(map((details) => ({ logicalDeviceId: multiMerchantLogicalDeviceId, details })))
          : null,
      [isDmMultiMerchantEnabled, isOpen, multiMerchantLogicalDeviceId],
    ),
  );

  const isGroupedDevice = isGroupedMultiMerchantSyncTarget(multiMerchantLogicalDeviceId, syncTargetDetails);

  const handleClose = useCallback(() => {
    setSyncConfigurationState({
      isLoading: false,
      isError: false,
      isSuccess: false,
      error: null,
      data: undefined,
    });
    onClose();
  }, [onClose, setSyncConfigurationState]);

  const handleSync = useCallback(
    (): Promise<void> =>
      new Promise((resolve, reject) => {
        requestAndUpdateState(
          deviceManagementBusinessService.triggerDeploymentRequests([...logicalDeviceIds]),
          setSyncConfigurationState,
          {
            onSuccess: () => {
              showNotification({
                content:
                  logicalDeviceIds.length === 1
                    ? toString(t('success.syncConfiguration'))
                    : t('success.syncConfigurationMultiple', {
                        ns: ALL_LNGS_NAMESPACES.LOGICAL_DEVICE,
                        count: logicalDeviceIds.length,
                      }),
                level: Level.Success,
              });
              handleClose();
              onSuccess?.();
              resolve(void 0);
            },
            onError: reject,
          },
        );
      }),
    [deviceManagementBusinessService, handleClose, logicalDeviceIds, onSuccess, setSyncConfigurationState, t],
  );

  return (
    <LogicalDeviceSyncConfigurationModal
      errorMessage={t('error.default.syncConfiguration')}
      isError={syncConfigurationState.isError}
      isGroupedDevice={isGroupedDevice}
      isLoading={syncConfigurationState.isLoading}
      isOpen={isOpen}
      onCancel={handleClose}
      onSync={handleSync}
      variant={variant}
    />
  );
}

function ExposedLogicalDeviceSyncConfigurationModalCpnt(props: LogicalDeviceSyncConfigurationModalProps): ReactElement {
  // Pre-initialize the section container on a fresh store before any child renders.
  // @ingenico/react-section-utils reads sectionContainerAtom synchronously during render and
  // throws if it is null, so the value must be set before the Provider mounts its children.
  const store = useMemo(() => {
    const sectionStore = createStore();
    sectionStore.set(sectionContainerAtom, deviceManagementContainer);
    return sectionStore;
  }, []);

  return (
    <Provider store={store}>
      <ExposedLogicalDeviceSyncConfigurationModalContent {...props} />
    </Provider>
  );
}

export const ExposedLogicalDeviceSyncConfigurationModal = memo(
  ExposedLogicalDeviceSyncConfigurationModalCpnt,
  deepEqual,
);
